import { MapUtils } from '../../utility/map-utils.js';
import { Currency } from '../serializable/currency.js';
import { Price } from '../serializable/price.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';

export class PriceRange extends DataModel {
  static readonly TYPE_ID: string = '4a74e5cd06e740aaa7a89452eb2c70e9';

  constructor(
    public from: Price,
    public to: Price,
  ) {
    super();
  }

  get isExact(): boolean {
    return this.from.value === this.to.value;
  }

  get $typeId(): string {
    return PriceRange.TYPE_ID;
  }

  static between(from: Price, to: Price): PriceRange {
    return new PriceRange(from, to);
  }

  static exactly(price: Price): PriceRange {
    return new PriceRange(price, price);
  }

  static free(currency: Currency): PriceRange {
    return PriceRange.exactly(Price.zero(currency));
  }

  static zero(currency: Currency): PriceRange {
    return PriceRange.exactly(Price.zero(currency));
  }

  static fromMap(context: SerializationContext, map: SerializedData): PriceRange {
    return new PriceRange(
        Price.decode(MapUtils.get(map, 'from')),
        Price.decode(MapUtils.get(map, 'to')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'from': this.from.encode(),
      'to': this.to.encode(),
    };
  }

  clone(): this {
    return new PriceRange(
        this.from.clone(),
        this.to.clone(),
    ) as this;
  }

  isSame(other: PriceRange): boolean {
    if(this === other) {
      return true;
    }

    return other.from === this.from && other.to === this.to;
  }

  copyWith(from: Price, to: Price): PriceRange {
    return new PriceRange(
        from ?? this.from.clone(),
        to ?? this.to.clone(),
    );
  }

  add(other: PriceRange): PriceRange {
    return new PriceRange(
        this.from.add(other.from),
        this.to.add(other.to),
    );
  }

  subtract(other: PriceRange): PriceRange {
    return new PriceRange(
        this.from.add(other.from.negative()),
        this.to.add(other.to.negative()),
    );
  }

  scale(scalar: number): PriceRange {
    return new PriceRange(
        this.from.scale(scalar),
        this.to.scale(scalar),
    );
  }

  equals(other: PriceRange): boolean {
    return this.from.currency === other.from.currency &&
    this.from.value === other.from.value &&
    this.to.currency === other.to.currency &&
    this.to.value === other.to.value;
  }

  toNonNegative() {
    if(this.from.isNegative()) {
      this.from = Price.zero(this.from.currency);
    }

    if(this.to.isNegative()) {
      this.to = Price.zero(this.to.currency);
    }
  }
}

export class PriceRangeBuilder extends DataModelBuilder<PriceRange> {
  build(context: SerializationContext, map: SerializedData): PriceRange {
    return PriceRange.fromMap(context, map);
  }
}
